import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Tabs, PeriodPicker, DashboardRangeBar, TabSection, TimezoneToggle, BROWSER_TIMEZONE } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import OverviewTab from '../components/dashboard/OverviewTab';
import FinancialTab from '../components/dashboard/FinancialTab';
import ShopsTab from '../components/dashboard/ShopsTab';
import RoadblocksTab from '../components/dashboard/RoadblocksTab';
import { useAuth } from '../hooks/useAuth';
import {
  useDashboardV2Financial,
  useDashboardV2Overview,
  useDashboardV2Roadblocks,
  useDashboardV2Shops,
} from '../hooks/useDashboardV2';
import type { DashboardV2Period, PortalDashboardV2Envelope } from '../services/api';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'financial', label: 'Financials' },
  { value: 'shops', label: 'Shops' },
  { value: 'roadblocks', label: 'Roadblocks' },
];

const VALID_PERIODS = new Set<string>(['today', 'week', 'month']);

function parsePeriod(raw: string | null): DashboardV2Period {
  return VALID_PERIODS.has(raw || '') ? (raw as DashboardV2Period) : 'today';
}

export default function DashboardPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shopsPage, setShopsPage] = useState(1);

  const tab = TABS.some((t) => t.value === searchParams.get('tab'))
    ? (searchParams.get('tab') as string)
    : 'overview';
  const period = parsePeriod(searchParams.get('period'));
  const useLocalTz = searchParams.get('tz') === 'local';
  const viewTz = useLocalTz ? BROWSER_TIMEZONE : undefined;

  // Overview stays enabled on every tab so the roadblocks badge can populate.
  const overviewQuery = useDashboardV2Overview(period, viewTz, true);
  const financialQuery = useDashboardV2Financial(period, viewTz, tab === 'financial');
  const shopsQuery = useDashboardV2Shops(period, shopsPage, viewTz, tab === 'shops');
  const roadblocksQuery = useDashboardV2Roadblocks(period, viewTz, tab === 'roadblocks');

  const activeQuery =
    tab === 'overview'
      ? overviewQuery
      : tab === 'financial'
        ? financialQuery
        : tab === 'shops'
          ? shopsQuery
          : roadblocksQuery;

  const roadblocksBadge = overviewQuery.data?.extras.roadblocksBadgeCount ?? 0;

  const tabsWithBadge = useMemo(
    () =>
      TABS.map((t) =>
        t.value === 'roadblocks' && roadblocksBadge > 0
          ? { ...t, label: `Roadblocks (${roadblocksBadge})` }
          : t,
      ),
    [roadblocksBadge],
  );

  const openCreditPurchaseFromNav =
    (location.state as { openCreditPurchase?: boolean } | null)?.openCreditPurchase === true;

  useEffect(() => {
    if (!openCreditPurchaseFromNav) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [openCreditPurchaseFromNav, location.pathname, navigate]);

  useEffect(() => {
    setShopsPage(1);
  }, [period, tab]);

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const setPeriod = useCallback(
    (next: DashboardV2Period) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('period', next);
        params.delete('from');
        params.delete('to');
        return params;
      });
    },
    [setSearchParams],
  );

  const costRate =
    user?.costRate != null && Number.isFinite(Number(user.costRate)) ? Number(user.costRate) : 0;

  return (
    <AppLayout>
      <div className="space-y-6 rounded-2xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">Dashboard</h1>
            <p className="mt-1 text-sm text-gray-600">Shop pulse and money at a glance</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-start sm:self-center">
            <Star className="h-4 w-4 text-blue-600" fill="currentColor" aria-hidden />
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800">
              Agent
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker value={period} onChange={setPeriod} accent="blue" />
            <TimezoneToggle
              accent="blue"
              defaultLabel="Report time"
              useLocal={useLocalTz}
              onChange={(local) => {
                setSearchParams((prev) => {
                  const params = new URLSearchParams(prev);
                  if (local) params.set('tz', 'local');
                  else params.delete('tz');
                  return params;
                });
              }}
            />
          </div>
        </div>

        <DashboardRangeBar
          envelope={activeQuery.data as PortalDashboardV2Envelope | undefined}
          onRefresh={activeQuery.refresh}
          refreshing={activeQuery.refreshing || activeQuery.isFetching}
          accent="blue"
        />

        <Tabs items={tabsWithBadge} value={tab} onChange={setTab} accent="blue" />

        <TabSection
          error={activeQuery.error}
          onRetry={activeQuery.refetch}
          isLoading={activeQuery.isLoading}
        >
          {tab === 'overview' && (
            <OverviewTab
              period={period}
              costRate={costRate}
              defaultOpenCreditPurchase={openCreditPurchaseFromNav}
              data={overviewQuery.data}
              isLoading={overviewQuery.isLoading}
            />
          )}
          {tab === 'financial' && (
            <FinancialTab
              period={period}
              data={financialQuery.data}
              isLoading={financialQuery.isLoading}
            />
          )}
          {tab === 'shops' && (
            <ShopsTab
              period={period}
              data={shopsQuery.data}
              isLoading={shopsQuery.isLoading}
              page={shopsPage}
              onPageChange={setShopsPage}
            />
          )}
          {tab === 'roadblocks' && (
            <RoadblocksTab
              period={period}
              data={roadblocksQuery.data}
              isLoading={roadblocksQuery.isLoading}
            />
          )}
        </TabSection>
      </div>
    </AppLayout>
  );
}
